import React, { useState, useEffect, useCallback } from 'react';
import { searchTRUDMedications, assignMedicationToUser } from '../../services/medicationService';
import { useAuth } from '../../context/AuthContext';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';

const AssignMedicationModal = ({ serverId, onClose, onSuccess }) => {
    const { currentUser } = useAuth();
    
    // Steps: 1 = Search, 1.5 = Custom Med, 2 = Select Users, 3 = Settings (Times/Quantities)
    const [step, setStep] = useState(1);
    
    // --- Search & Filter State ---
    const [searchQuery, setSearchQuery] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [searchResults, setSearchResults] = useState([]);
    const [selectedMedicine, setSelectedMedicine] = useState(null);
    
    // TRUD Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [totalResults, setTotalResults] = useState(0);

    // TRUD Advanced Filters
    const [showFilters, setShowFilters] = useState(false);
    const [filters, setFilters] = useState({
        type: '',
        dosage: '',
        quantity: '',
        formulation: '',
        manufacturer: '',
        brand: '',
        trudId: ''
    });

    // --- Custom Medication State (Step 1.5) ---
    const [customMed, setCustomMed] = useState({
        med_name: '',
        type: 'VMP',
        dosage: '',
        unit: 'tablet',
        packSize: '',
        routeOfAdministration: 'Oral'
    });
    
    // --- User Selection State (Step 2) ---
    const [availableUsers, setAvailableUsers] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [isLoadingUsers, setIsLoadingUsers] = useState(false);
    
    // --- Scheduling Settings State (Step 3) ---
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [timings, setTimings] = useState(['08:00']);
    const [dosageAmount, setDosageAmount] = useState('1'); // Amount per dose
    const [requiredQuantity, setRequiredQuantity] = useState(30);
    const [instructions, setInstructions] = useState('');
    const [isControlled, setIsControlled] = useState(false);
    const [isPRN, setIsPRN] = useState(false);
    const [route, setRoute] = useState('Oral'); // Default
    
    const [isSaving, setIsSaving] = useState(false);

    // Set today as default start date
    useEffect(() => {
        const today = new Date().toISOString().split('T')[0];
        setStartDate(today);
    }, []);

    // Fetch users for step 2
    useEffect(() => {
        const fetchUsers = async () => {
            setIsLoadingUsers(true);
            try {
                const usersRef = collection(db, 'servers', serverId, 'serviceUsers');
                const q = query(usersRef, where('status', '==', true));
                const snap = await getDocs(q);
                const users = [];
                snap.forEach(d => users.push({ id: d.id, ...d.data() }));
                setAvailableUsers(users);
            } catch (err) {
                console.error("Failed to fetch users", err);
            } finally {
                setIsLoadingUsers(false);
            }
        };
        fetchUsers();
    }, [serverId]);

    // Debounced TRUD Search Execution
    const executeSearch = useCallback(async (term, pageNum = 1, activeFilters = filters) => {
        if (term.length < 3) return;
        
        setIsSearching(true);
        try {
            // Replicates Flutter's search calling cloud function with filters and pagination
            const { results, meta } = await searchTRUDMedications(term, pageNum, activeFilters);
            setSearchResults(results);
            setTotalResults(meta.total || results.length);
            setCurrentPage(pageNum);
        } catch (err) {
            console.error("TRUD Search Error", err);
        } finally {
            setIsSearching(false);
        }
    }, [filters]);

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (searchQuery.length >= 3) {
                executeSearch(searchQuery, 1, filters);
            } else {
                setSearchResults([]);
                setTotalResults(0);
            }
        }, 500);

        return () => clearTimeout(delayDebounceFn);
    }, [searchQuery, filters, executeSearch]); // Triggers when query or filters change

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
    };

    const handleNextPage = () => {
        if (searchResults.length === 20) { // Assuming limit 20
            executeSearch(searchQuery, currentPage + 1, filters);
        }
    };

    const handlePrevPage = () => {
        if (currentPage > 1) {
            executeSearch(searchQuery, currentPage - 1, filters);
        }
    };

    const handleCustomMedSubmit = () => {
        if (!customMed.med_name.trim()) {
            alert("Medicine name is required");
            return;
        }
        // Normalize custom med into same structure as TRUD result
        const selected = {
            id: 'custom_' + Date.now(),
            med_name: customMed.med_name,
            type: customMed.type,
            dosage: customMed.dosage,
            unit: customMed.unit,
            packSize: customMed.packSize,
            routeOfAdministration: customMed.routeOfAdministration,
            source: 'Custom',
            manufacturer: 'N/A'
        };
        setSelectedMedicine(selected);
        setRoute(customMed.routeOfAdministration);
        setStep(2); // Proceed to select users
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            const adminId = currentUser?.uid;
            const adminName = currentUser?.email || 'Unknown Admin';
            
            // Build the comprehensive medication data map mapping exactly to what flutter saves
            const medData = {
                ...selectedMedicine,
                startDate,
                endDate,
                timings,
                dosageAmount,
                requiredQuantity,
                isControlled,
                isPRN,
                instructions,
                route
            };
            
            // Replicate batch save for each user (loops through users and creates myMedications/medicineStocks)
            for (const u of selectedUsers) {
                await assignMedicationToUser(serverId, u.id, u.fullName || u.name, adminId, adminName, medData);
            }
            
            onSuccess();
        } catch (err) {
            console.error("Failed to assign medication", err);
            alert("Failed to assign: " + err.message);
        } finally {
            setIsSaving(false);
        }
    };

    const toggleUser = (u) => {
        if (selectedUsers.find(su => su.id === u.id)) {
            setSelectedUsers(selectedUsers.filter(su => su.id !== u.id));
        } else {
            setSelectedUsers([...selectedUsers, u]);
        }
    };

    return (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
            <div style={{ backgroundColor: 'white', borderRadius: '12px', width: '850px', maxWidth: '95%', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                
                {/* Header */}
                <div style={{ padding: '20px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h2 style={{ margin: 0, color: '#2d3748', fontSize: '20px' }}>
                        {step === 1 ? 'Search TRUD Library' : 
                         step === 1.5 ? 'Add Custom Medication' :
                         step === 2 ? 'Select Users' : 'Medication Settings'}
                    </h2>
                    <button onClick={onClose} style={{ border: 'none', background: 'transparent', fontSize: '20px', cursor: 'pointer', color: '#718096' }}>×</button>
                </div>

                {/* Body */}
                <div style={{ padding: '20px', overflowY: 'auto', flex: 1 }}>
                    
                    {/* STEP 1: TRUD SEARCH */}
                    {step === 1 && (
                        <div>
                            <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                                <input 
                                    type="text"
                                    placeholder="Search by medication name (min 3 chars)..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    style={{ flex: 1, padding: '12px', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '16px' }}
                                    autoFocus
                                />
                                <button 
                                    onClick={() => setShowFilters(!showFilters)}
                                    style={{ padding: '0 15px', borderRadius: '6px', border: '1px solid #cbd5e0', backgroundColor: showFilters ? '#edf2f7' : 'white', cursor: 'pointer' }}
                                >
                                    Filters {showFilters ? '▲' : '▼'}
                                </button>
                            </div>

                            {showFilters && (
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', padding: '15px', backgroundColor: '#f7fafc', borderRadius: '6px', marginBottom: '20px' }}>
                                    <div>
                                        <label style={{ display: 'block', fontSize: '12px', color: '#718096', marginBottom: '4px' }}>Type (e.g. VMP, AMP)</label>
                                        <input type="text" value={filters.type} onChange={(e) => handleFilterChange('type', e.target.value)} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #e2e8f0' }} />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', fontSize: '12px', color: '#718096', marginBottom: '4px' }}>Dosage / Strength</label>
                                        <input type="text" value={filters.dosage} onChange={(e) => handleFilterChange('dosage', e.target.value)} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #e2e8f0' }} />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', fontSize: '12px', color: '#718096', marginBottom: '4px' }}>Formulation</label>
                                        <input type="text" value={filters.formulation} onChange={(e) => handleFilterChange('formulation', e.target.value)} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #e2e8f0' }} />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', fontSize: '12px', color: '#718096', marginBottom: '4px' }}>Brand</label>
                                        <input type="text" value={filters.brand} onChange={(e) => handleFilterChange('brand', e.target.value)} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #e2e8f0' }} />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', fontSize: '12px', color: '#718096', marginBottom: '4px' }}>Manufacturer</label>
                                        <input type="text" value={filters.manufacturer} onChange={(e) => handleFilterChange('manufacturer', e.target.value)} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #e2e8f0' }} />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', fontSize: '12px', color: '#718096', marginBottom: '4px' }}>TRUD ID</label>
                                        <input type="text" value={filters.trudId} onChange={(e) => handleFilterChange('trudId', e.target.value)} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #e2e8f0' }} />
                                    </div>
                                </div>
                            )}
                            
                            {isSearching ? (
                                <div style={{ textAlign: 'center', padding: '40px', color: '#718096' }}>Searching TRUD Database...</div>
                            ) : searchResults.length > 0 ? (
                                <div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', fontSize: '14px', color: '#718096' }}>
                                        <span>Showing page {currentPage} (Total: {totalResults > 20 ? '20+' : totalResults})</span>
                                        <div style={{ display: 'flex', gap: '10px' }}>
                                            <button onClick={handlePrevPage} disabled={currentPage === 1} style={{ border: 'none', background: 'none', color: currentPage === 1 ? '#cbd5e0' : '#3182ce', cursor: 'pointer' }}>&laquo; Prev</button>
                                            <button onClick={handleNextPage} disabled={searchResults.length < 20} style={{ border: 'none', background: 'none', color: searchResults.length < 20 ? '#cbd5e0' : '#3182ce', cursor: 'pointer' }}>Next &raquo;</button>
                                        </div>
                                    </div>

                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        {searchResults.map((med, idx) => (
                                            <div 
                                                key={idx}
                                                onClick={() => {
                                                    setSelectedMedicine(med);
                                                    setRoute(med.routeOfAdministration || 'Oral');
                                                    setStep(2);
                                                }}
                                                style={{ 
                                                    padding: '12px', 
                                                    border: '1px solid #e2e8f0', 
                                                    borderRadius: '6px', 
                                                    cursor: 'pointer',
                                                    backgroundColor: selectedMedicine?.id === med.id ? '#ebf8ff' : 'white',
                                                    borderColor: selectedMedicine?.id === med.id ? '#3182ce' : '#e2e8f0'
                                                }}
                                            >
                                                <div style={{ fontWeight: 'bold', color: '#2d3748' }}>{med.med_name}</div>
                                                <div style={{ fontSize: '13px', color: '#718096', display: 'flex', flexWrap: 'wrap', gap: '15px', marginTop: '6px' }}>
                                                    {med.id && <span><strong>ID:</strong> {med.id}</span>}
                                                    {med.type && <span><strong>Type:</strong> {med.type}</span>}
                                                    {med.dosage && <span><strong>Dose:</strong> {med.dosage}</span>}
                                                    {med.unit && <span><strong>Form:</strong> {med.unit}</span>}
                                                    {med.manufacturer && <span><strong>Mfr:</strong> {med.manufacturer}</span>}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : searchQuery.length >= 3 ? (
                                <div style={{ textAlign: 'center', padding: '40px', color: '#718096' }}>
                                    <p>No medications found in TRUD. Try adjusting your search/filters.</p>
                                    <button 
                                        onClick={() => setStep(1.5)}
                                        style={{ marginTop: '15px', padding: '10px 20px', backgroundColor: '#edf2f7', border: '1px solid #cbd5e0', borderRadius: '6px', cursor: 'pointer', color: '#2d3748', fontWeight: 'bold' }}
                                    >
                                        + Add Custom Medication
                                    </button>
                                </div>
                            ) : (
                                <div style={{ textAlign: 'center', padding: '40px', color: '#718096' }}>
                                    Enter at least 3 characters to search the NHS TRUD database.
                                    <div style={{ marginTop: '20px' }}>
                                        <button onClick={() => setStep(1.5)} style={{ border: 'none', background: 'none', color: '#3182ce', textDecoration: 'underline', cursor: 'pointer' }}>Skip and manually add a medication</button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* STEP 1.5: CUSTOM MEDICATION FALLBACK */}
                    {step === 1.5 && (
                        <div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                <div>
                                    <label style={{ display: 'block', fontWeight: '600', marginBottom: '5px' }}>Medicine Name *</label>
                                    <input type="text" value={customMed.med_name} onChange={e => setCustomMed({...customMed, med_name: e.target.value})} style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #e2e8f0' }} />
                                </div>
                                <div style={{ display: 'flex', gap: '15px' }}>
                                    <div style={{ flex: 1 }}>
                                        <label style={{ display: 'block', fontWeight: '600', marginBottom: '5px' }}>Type (e.g. VMP, AMP)</label>
                                        <input type="text" value={customMed.type} onChange={e => setCustomMed({...customMed, type: e.target.value})} style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #e2e8f0' }} />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <label style={{ display: 'block', fontWeight: '600', marginBottom: '5px' }}>Dosage (e.g. 500mg)</label>
                                        <input type="text" value={customMed.dosage} onChange={e => setCustomMed({...customMed, dosage: e.target.value})} style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #e2e8f0' }} />
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: '15px' }}>
                                    <div style={{ flex: 1 }}>
                                        <label style={{ display: 'block', fontWeight: '600', marginBottom: '5px' }}>Formulation (e.g. Tablet)</label>
                                        <input type="text" value={customMed.unit} onChange={e => setCustomMed({...customMed, unit: e.target.value})} style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #e2e8f0' }} />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <label style={{ display: 'block', fontWeight: '600', marginBottom: '5px' }}>Pack Size</label>
                                        <input type="text" value={customMed.packSize} onChange={e => setCustomMed({...customMed, packSize: e.target.value})} style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #e2e8f0' }} />
                                    </div>
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontWeight: '600', marginBottom: '5px' }}>Route of Administration</label>
                                    <select value={customMed.routeOfAdministration} onChange={e => setCustomMed({...customMed, routeOfAdministration: e.target.value})} style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #e2e8f0', backgroundColor: 'white' }}>
                                        <option value="Oral">Oral</option>
                                        <option value="Topical">Topical</option>
                                        <option value="Inhalation">Inhalation</option>
                                        <option value="Transdermal">Transdermal Patch</option>
                                        <option value="Subcutaneous">Subcutaneous (Injection)</option>
                                        <option value="Intramuscular">Intramuscular</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* STEP 2: SELECT USERS */}
                    {step === 2 && (
                        <div>
                            <div style={{ marginBottom: '15px', padding: '12px', backgroundColor: '#ebf8ff', borderRadius: '6px', color: '#2b6cb0' }}>
                                <strong>Selected Medication:</strong> {selectedMedicine?.med_name}
                                {selectedMedicine?.source === 'Custom' && <span style={{ marginLeft: '10px', fontSize: '12px', padding: '2px 6px', backgroundColor: '#fed7d7', color: '#c53030', borderRadius: '4px' }}>Custom Entry</span>}
                            </div>
                            
                            <h3 style={{ fontSize: '16px', color: '#4a5568', marginBottom: '10px' }}>Select Users to Assign To:</h3>
                            
                            {isLoadingUsers ? (
                                <div>Loading users...</div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    {availableUsers.map(u => {
                                        const isSelected = !!selectedUsers.find(su => su.id === u.id);
                                        return (
                                            <label key={u.id} style={{ display: 'flex', alignItems: 'center', padding: '12px', border: '1px solid #e2e8f0', borderRadius: '6px', cursor: 'pointer', backgroundColor: isSelected ? '#ebf8ff' : 'white' }}>
                                                <input 
                                                    type="checkbox" 
                                                    checked={isSelected}
                                                    onChange={() => toggleUser(u)}
                                                    style={{ marginRight: '15px', transform: 'scale(1.2)' }}
                                                />
                                                <div style={{ fontWeight: '500', color: '#2d3748' }}>{u.fullName || u.name || 'Unnamed User'}</div>
                                            </label>
                                        )
                                    })}
                                </div>
                            )}
                        </div>
                    )}

                    {/* STEP 3: ENHANCED SETTINGS */}
                    {step === 3 && (
                        <div>
                            <div style={{ marginBottom: '15px', padding: '12px', backgroundColor: '#ebf8ff', borderRadius: '6px', color: '#2b6cb0' }}>
                                Assigning <strong>{selectedMedicine?.med_name}</strong> to <strong>{selectedUsers.length}</strong> user(s).
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                                
                                {/* Column 1: Dates & Timings */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                    <div>
                                        <label style={{ display: 'block', fontWeight: '600', marginBottom: '5px', color: '#4a5568' }}>Start Date</label>
                                        <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={{ padding: '8px', borderRadius: '4px', border: '1px solid #e2e8f0', width: '100%' }} />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', fontWeight: '600', marginBottom: '5px', color: '#4a5568' }}>End Date (Optional)</label>
                                        <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} style={{ padding: '8px', borderRadius: '4px', border: '1px solid #e2e8f0', width: '100%' }} />
                                    </div>

                                    <div>
                                        <label style={{ display: 'flex', alignItems: 'center', fontWeight: '600', color: '#4a5568', cursor: 'pointer', marginBottom: '5px' }}>
                                            <input type="checkbox" checked={isPRN} onChange={(e) => setIsPRN(e.target.checked)} style={{ marginRight: '10px' }} />
                                            PRN (As Needed)
                                        </label>
                                    </div>

                                    {!isPRN && (
                                        <div>
                                            <label style={{ display: 'block', fontWeight: '600', marginBottom: '5px', color: '#4a5568' }}>Timings (HH:MM)</label>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                                                {timings.map((t, idx) => (
                                                    <input 
                                                        key={idx}
                                                        type="time" 
                                                        value={t}
                                                        onChange={(e) => {
                                                            const newT = [...timings];
                                                            newT[idx] = e.target.value;
                                                            setTimings(newT);
                                                        }}
                                                        style={{ padding: '8px', borderRadius: '4px', border: '1px solid #e2e8f0' }}
                                                    />
                                                ))}
                                                <button 
                                                    onClick={() => setTimings([...timings, '12:00'])}
                                                    style={{ padding: '8px 12px', backgroundColor: '#edf2f7', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                                                >+ Time</button>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Column 2: Dosage, Stock, Instructions */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                    <div style={{ display: 'flex', gap: '10px' }}>
                                        <div style={{ flex: 1 }}>
                                            <label style={{ display: 'block', fontWeight: '600', marginBottom: '5px', color: '#4a5568' }}>Amount per Dose</label>
                                            <input type="text" placeholder="e.g. 2 tabs" value={dosageAmount} onChange={(e) => setDosageAmount(e.target.value)} style={{ padding: '8px', borderRadius: '6px', border: '1px solid #e2e8f0', width: '100%' }} />
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <label style={{ display: 'block', fontWeight: '600', marginBottom: '5px', color: '#4a5568' }}>Total Quantity Rx</label>
                                            <input type="number" value={requiredQuantity} onChange={(e) => setRequiredQuantity(parseInt(e.target.value, 10) || 0)} style={{ padding: '8px', borderRadius: '6px', border: '1px solid #e2e8f0', width: '100%' }} />
                                        </div>
                                    </div>
                                    
                                    <div>
                                        <label style={{ display: 'block', fontWeight: '600', marginBottom: '5px', color: '#4a5568' }}>Route of Administration</label>
                                        <select value={route} onChange={(e) => setRoute(e.target.value)} style={{ padding: '10px', borderRadius: '6px', border: '1px solid #e2e8f0', width: '100%', backgroundColor: 'white' }}>
                                            <option value="Oral">Oral</option>
                                            <option value="Topical">Topical</option>
                                            <option value="Inhalation">Inhalation</option>
                                            <option value="Transdermal">Transdermal Patch</option>
                                            <option value="Subcutaneous">Subcutaneous (Injection)</option>
                                            <option value="Intramuscular">Intramuscular</option>
                                            <option value="Ophthalmic">Ophthalmic (Eye)</option>
                                            <option value="Otic">Otic (Ear)</option>
                                            <option value="Rectal">Rectal</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label style={{ display: 'block', fontWeight: '600', marginBottom: '5px', color: '#4a5568' }}>Specific Instructions</label>
                                        <textarea 
                                            placeholder="e.g. Take after food"
                                            value={instructions}
                                            onChange={(e) => setInstructions(e.target.value)}
                                            style={{ padding: '8px', borderRadius: '6px', border: '1px solid #e2e8f0', width: '100%', minHeight: '60px' }}
                                        />
                                    </div>

                                    <div>
                                        <label style={{ display: 'flex', alignItems: 'center', fontWeight: '600', color: '#e53e3e', cursor: 'pointer' }}>
                                            <input type="checkbox" checked={isControlled} onChange={(e) => setIsControlled(e.target.checked)} style={{ marginRight: '10px', transform: 'scale(1.2)' }} />
                                            This is a Controlled Drug (CD)
                                        </label>
                                    </div>
                                </div>

                            </div>
                        </div>
                    )}
                </div>

                {/* Footer Controls */}
                <div style={{ padding: '20px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', backgroundColor: '#f7fafc' }}>
                    <button 
                        onClick={() => {
                            if (step === 1.5 || step === 2) setStep(1);
                            else if (step === 3) setStep(2);
                            else onClose();
                        }}
                        style={{ padding: '10px 20px', border: 'none', backgroundColor: 'transparent', color: '#4a5568', fontWeight: 'bold', cursor: 'pointer' }}
                    >
                        {step === 1 ? 'Cancel' : 'Back'}
                    </button>
                    
                    {step === 1 && (
                        <button 
                            disabled={!selectedMedicine}
                            onClick={() => setStep(2)}
                            style={{ padding: '10px 20px', border: 'none', borderRadius: '6px', backgroundColor: selectedMedicine ? '#3182ce' : '#cbd5e0', color: 'white', fontWeight: 'bold', cursor: selectedMedicine ? 'pointer' : 'not-allowed' }}
                        >
                            Next: Select Users
                        </button>
                    )}

                    {step === 1.5 && (
                        <button 
                            onClick={handleCustomMedSubmit}
                            style={{ padding: '10px 20px', border: 'none', borderRadius: '6px', backgroundColor: '#3182ce', color: 'white', fontWeight: 'bold', cursor: 'pointer' }}
                        >
                            Use Custom Medication
                        </button>
                    )}
                    
                    {step === 2 && (
                        <button 
                            disabled={selectedUsers.length === 0}
                            onClick={() => setStep(3)}
                            style={{ padding: '10px 20px', border: 'none', borderRadius: '6px', backgroundColor: selectedUsers.length > 0 ? '#3182ce' : '#cbd5e0', color: 'white', fontWeight: 'bold', cursor: selectedUsers.length > 0 ? 'pointer' : 'not-allowed' }}
                        >
                            Next: Settings
                        </button>
                    )}
                    
                    {step === 3 && (
                        <button 
                            disabled={isSaving}
                            onClick={handleSave}
                            style={{ padding: '10px 20px', border: 'none', borderRadius: '6px', backgroundColor: '#38a169', color: 'white', fontWeight: 'bold', cursor: isSaving ? 'not-allowed' : 'pointer' }}
                        >
                            {isSaving ? 'Saving...' : 'Assign Medication'}
                        </button>
                    )}
                </div>

            </div>
        </div>
    );
};

export default AssignMedicationModal;
